import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jsonDeleteResponse, jsonGet, jsonPost, jsonPut } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { fetchAndStorePermissions } from './useAuth';

const EMPTY_MATRIX = { views: [], roles: [], grants: [] };

async function invalidatePermissionQueries(queryClient) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.permissions.all() });
    await queryClient.invalidateQueries({ queryKey: queryKeys.roles.all() });
    await fetchAndStorePermissions();
}

export function usePermissionMatrix({ enabled = true } = {}) {
    return useQuery({
        queryKey: queryKeys.permissions.matrix(),
        queryFn: () => jsonGet('/api/Permissions/Matrix'),
        enabled,
        staleTime: 0,
    });
}

export function usePermissionMatrixData(options) {
    const query = usePermissionMatrix(options);
    return {
        ...query,
        matrix: query.data ?? EMPTY_MATRIX,
    };
}

export function usePutGrant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ roleId, appViewId, conditions = null }) =>
            jsonPut('/api/Permissions/Grant', { roleId, appViewId, conditions }),
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useRevokeGrant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (grantId) => {
            const res = await jsonDeleteResponse(`/api/Permissions/RevokeGrant/${grantId}`);
            return res.data;
        },
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useCreateRole() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (name) => jsonPost('/api/Permissions/CreateRole', { name }),
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useUpdateRoleName() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ roleId, name }) =>
            jsonPut('/api/Permissions/UpdateRole', { roleId, name }),
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useDeleteRole() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (roleId) => {
            const res = await jsonDeleteResponse(`/api/Permissions/DeleteRole/${roleId}`);
            return res.data;
        },
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useReorderRoles() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (roleIdsInOrder) =>
            jsonPut('/api/Permissions/ReorderRoles', { roleIdsInOrder }),
        onSuccess: () => invalidatePermissionQueries(queryClient),
    });
}

export function useRoleUserCounts({ enabled = true } = {}) {
    return useQuery({
        queryKey: queryKeys.permissions.roleUserCounts(),
        queryFn: () => jsonGet('/api/Permissions/RoleUserCounts'),
        enabled,
        staleTime: 0,
    });
}

export function useUsersForRole(roleId, { enabled = true } = {}) {
    return useQuery({
        queryKey: queryKeys.permissions.roleUsers(roleId),
        queryFn: () => jsonGet(`/api/Account/UsersForRole/${roleId}`),
        enabled: enabled && Boolean(roleId),
        staleTime: 0,
    });
}
